"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { useOrgMemberships } from "@/hooks/use-org";
import { Icon } from "@/components/ui/icon";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/features/auth";
import { Select } from "@/components/ui/select";
import { CreateOrganization } from "@/components/layout/navbar/create-organization";
import { useSwitchOrganization } from "@/components/layout/navbar/use-switch-organization";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 8;

export function OrganizationSelect({
  showLabel = false,
  className,
  appearance = "navbar",
  collapsed = false,
}: {
  showLabel?: boolean;
  className?: string;
  /**
   * `navbar` — compact muted pill (default).
   * `sidebar` — full-width ghost workspace row for the sidebar header: avatar
   * tile + name + grabber; `collapsed` reduces it to the avatar tile alone.
   */
  appearance?: "navbar" | "sidebar";
  collapsed?: boolean;
}) {
  const isSidebar = appearance === "sidebar";
  const { organization } = useAuth();
  const [open, setOpen] = useState(false);
  const switchOrg = useSwitchOrganization();

  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useOrgMemberships({
    enabled: open,
    search: debouncedSearch,
  });

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [open]);

  const memberships = useMemo(() => {
    const all = data?.memberships ?? [];
    return [...all].sort((a, b) => {
      if (a.is_active_context && !b.is_active_context) return -1;
      if (!a.is_active_context && b.is_active_context) return 1;
      return a.org_name.localeCompare(b.org_name);
    });
  }, [data]);

  const visibleOrgs = memberships.slice(0, visibleCount);
  const activeRole = memberships.find((m) => m.is_active_context)?.role ?? null;

  return (
    <Select.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setSearch("");
          setVisibleCount(PAGE_SIZE);
        }
        setOpen(nextOpen);
      }}
    >
      {isSidebar ? (
        <Select.Trigger
          size="none"
          variant="ghost"
          aria-label={
            organization
              ? `Organization: ${organization.name}. Switch organization`
              : "Select organization"
          }
          className={cn(
            "group/workspace h-9 min-w-0 cursor-pointer rounded-md transition-colors duration-150",
            "hover:bg-contrast/4 data-[state=open]:bg-contrast/6",
            "outline-none focus-visible:ring-2 focus-visible:ring-ai/40",
            collapsed ? "w-9 justify-center px-0" : "w-full gap-2 px-1.5",
            className,
          )}
        >
          <Avatar className="size-6 shrink-0 rounded-[6px] shadow-[inset_0_0_0_1px_var(--border-alpha)]">
            <AvatarImage
              src={organization?.logo_url}
              alt={organization?.name ?? ""}
            />
            <AvatarFallback className="rounded-[6px] bg-contrast/10">
              <Icon
                name="IconBuildings"
                size={12}
                className="text-heading opacity-40"
              />
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate text-left text-[13px] font-semibold leading-5 text-heading">
                {organization?.name ?? "Select organization"}
              </span>
              <Select.Icon
                size={14}
                className="ml-0 text-contrast/35 transition-colors group-hover/workspace:text-contrast/60"
              />
            </>
          )}
        </Select.Trigger>
      ) : (
        <Select.Trigger
          size="sm"
          variant="muted"
          className={cn("w-auto px-2 max-w-40", className)}
        >
          <div className="flex items-center gap-2 min-w-0">
            {organization ? (
              <>
                <Avatar className="shrink-0 rounded-sm size-5">
                  <AvatarImage
                    src={organization.logo_url}
                    alt={organization.name}
                  />
                  <AvatarFallback className="bg-contrast/10">
                    <Icon
                      name="IconBuildings"
                      size={12}
                      className="text-heading opacity-40"
                    />
                  </AvatarFallback>
                </Avatar>
                <span
                  className={cn(
                    "text-sm text-heading truncate",
                    showLabel ? "block" : "lg:block hidden",
                  )}
                >
                  {organization.name}
                </span>
              </>
            ) : (
              <span
                className={cn(
                  "text-sm text-heading truncate",
                  showLabel ? "block" : "lg:block hidden",
                )}
              >
                Select Organization
              </span>
            )}
          </div>
          <Select.Icon
            className={cn(
              "ml-auto shrink-0",
              showLabel ? "block" : "hidden lg:block",
            )}
          />
        </Select.Trigger>
      )}

      <Select.Content
        align={isSidebar ? "start" : "end"}
        sideOffset={isSidebar ? 6 : 4}
        matchTriggerWidth={false}
        className="w-[300px]"
      >
        {organization && (
          <>
            {/* Current-context header — identifies where you are before
                offering to switch (Linear / Runway workspace-switcher pattern) */}
            <div className="flex items-center gap-2.5 px-3 py-2.5">
              <Avatar className="size-8 shrink-0 rounded-md border border-contrast/8">
                <AvatarImage
                  src={organization.logo_url}
                  alt={organization.name}
                />
                <AvatarFallback className="bg-contrast/10 rounded-md">
                  <Icon
                    name="IconBuildings"
                    size={16}
                    className="text-heading opacity-40"
                  />
                </AvatarFallback>
              </Avatar>
              <div className="flex min-w-0 flex-1 flex-col gap-px">
                <span className="truncate text-sm font-medium leading-5 text-heading">
                  {organization.name}
                </span>
                <span className="truncate text-[11px] leading-4 text-contrast/40">
                  Current organization
                </span>
              </div>
              {activeRole && (
                <span className="shrink-0 rounded-md border border-contrast/8 bg-contrast/4 px-1.5 py-0.5 text-[10px] font-medium capitalize text-contrast/60">
                  {activeRole}
                </span>
              )}
            </div>
            <Select.Separator />
          </>
        )}
        <Select.Search
          ref={inputRef}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setVisibleCount(PAGE_SIZE);
          }}
          placeholder="Search organizations"
        />

        {isLoading && !data ? (
          <Select.ListPending />
        ) : memberships.length === 0 && !search ? (
          <Select.Empty>
            <Icon
              name="IconBuildings"
              size={32}
              className="text-heading opacity-40"
            />
            <Select.EmptyTitle>No organizations</Select.EmptyTitle>
            <Select.EmptyDescription>
              Switch between your organizations here.{" "}
              <span className="text-[#437dfc] cursor-pointer">Learn more</span>
            </Select.EmptyDescription>
          </Select.Empty>
        ) : visibleOrgs.length === 0 ? (
          <Select.Empty>
            <Icon
              name="IconMagnifyingGlass"
              size={32}
              className="text-heading opacity-40"
            />
            <Select.EmptyTitle>No results</Select.EmptyTitle>
            <Select.EmptyDescription>
              No organizations match &ldquo;{search}&rdquo;
            </Select.EmptyDescription>
          </Select.Empty>
        ) : (
          <Select.List className="max-h-80">
            {visibleOrgs.map((m) => (
              <Select.Item
                key={m.workos_org_id}
                selected={m.is_active_context}
                disabled={m.is_active_context || switchOrg.isPending}
                className={cn(
                  "h-auto gap-2.5 px-2 py-1.5",
                  m.is_active_context && "disabled:opacity-100",
                )}
                onClick={() => {
                  if (!m.is_active_context && !switchOrg.isPending) {
                    setOpen(false);
                    switchOrg.mutate({
                      data: { workos_org_id: m.workos_org_id },
                    });
                  }
                }}
              >
                <Avatar className="size-7 shrink-0 rounded-md border border-contrast/8">
                  <AvatarImage src={m.logo_url} alt={m.org_name} />
                  <AvatarFallback className="bg-contrast/10 rounded-md">
                    <Icon
                      name="IconBuildings"
                      size={14}
                      className="text-heading opacity-40"
                    />
                  </AvatarFallback>
                </Avatar>
                <span className="flex min-w-0 flex-1 flex-col items-start gap-px text-left">
                  <span
                    className={cn(
                      "w-full truncate text-sm leading-5 text-heading",
                      m.is_active_context && "font-medium",
                    )}
                  >
                    {m.org_name}
                  </span>
                  <span className="w-full truncate text-[11px] leading-4 capitalize text-contrast/40">
                    {m.role}
                  </span>
                </span>
                {m.is_active_context && (
                  <Icon
                    name="IconSubscriptionTick1"
                    size={16}
                    className="text-ai shrink-0"
                  />
                )}
              </Select.Item>
            ))}
            {visibleCount < memberships.length && (
              <Select.ShowMore
                remaining={memberships.length - visibleCount}
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              />
            )}
          </Select.List>
        )}

        <Select.Footer>
          <CreateOrganization />
        </Select.Footer>
      </Select.Content>
    </Select.Root>
  );
}
